import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import { useMemo, useState, useCallback } from 'react';
import { DateRange } from 'react-date-range';
import { useDidUpdate, useMediaMatch } from 'rooks';
import { useDateLocaleModule } from '../../hooks/useDateLocaleModule';
import { useI18n } from '../I18n';
import { Button } from '../Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, useDialog, } from '../Dialog';
import { createStaticRanges } from './staticRanges';
import { DateRangePickerInput } from './DateRangePickerInput';
import { DateRangePickerHeader } from './DateRangePickerHeader';
import { DateRangePickerRanges } from './DateRangePickerRanges';
import { MEDIA_QUERY } from '../../constants/breakpoints';
import * as styles from './DateRangePicker.module.scss';
const SELECTION_KEY = 'selectionKey';
const TODAY = new Date();
export const DateRangePicker = ({ startDate, endDate, minDate, maxDate, staticRanges, isPickerOpen, onChange, onPickerOpen, onPickerClose, placeholder, labelVariant, disabled, required, notification, mode, label, confirmButtonLabel, cancelButtonLabel, headerLabel, }) => {
    const i18n = useI18n();
    const isMobile = useMediaMatch(MEDIA_QUERY.only.Mobile);
    const [dates, setDates] = useState({
        startDate,
        endDate,
    });
    const { onClose, onOpen, onToggle, isOpen } = useDialog(isPickerOpen);
    const createdStaticRanges = useMemo(() => staticRanges
        ? createStaticRanges(staticRanges, minDate, maxDate)
        : undefined, [staticRanges, minDate, maxDate]);
    const handleOnSelectRange = (range) => {
        setDates({
            startDate: range.startDate || null,
            endDate: range.endDate || null,
        });
    };
    const handleOnChange = (ranges) => {
        handleOnSelectRange(ranges[SELECTION_KEY]);
    };
    const handleConfirmDate = useCallback(() => {
        onChange?.({
            startDate: dates.startDate || TODAY,
            endDate: dates.endDate,
        });
        onClose();
    }, [dates, onChange, onClose]);
    const dateLocaleModule = useDateLocaleModule();
    const ranges = [
        {
            startDate: dates.startDate || TODAY,
            endDate: dates.endDate ?? (dates.startDate || TODAY),
            key: SELECTION_KEY,
        },
    ];
    useDidUpdate(() => {
        if (isOpen) {
            onPickerOpen?.();
        }
        else {
            onPickerClose?.();
        }
    }, [isOpen, onPickerClose, onPickerOpen]);
    useDidUpdate(() => {
        if (isPickerOpen) {
            onOpen();
        }
        else {
            onClose();
        }
    }, [isPickerOpen, onClose, onOpen]);
    return (_jsxs(_Fragment, { children: [_jsx(DateRangePickerInput, { label: label || i18n.t('period'), labelVariant: labelVariant, placeholder: placeholder, disabled: disabled, required: required, notification: notification, mode: mode, startDate: startDate, endDate: endDate, onClick: onOpen, isMobile: isMobile }), _jsxs(Dialog, { open: isOpen, onToggle: onToggle, variant: "wide", children: [isMobile && (_jsx(DialogHeader, { onClose: onClose, children: headerLabel || i18n.t('select_your_dates') })), _jsx(DateRange, { ranges: ranges, onChange: handleOnChange, direction: isMobile ? 'vertical' : 'horizontal', scroll: isMobile ? { enabled: true } : undefined, minDate: minDate, maxDate: maxDate, navigatorRenderer: (_, changeShownDate) => isMobile ? (_jsx("div", {})) : (_jsx(DateRangePickerHeader, { decreaseMonth: () => changeShownDate(-1, 'monthOffset'), increaseMonth: () => changeShownDate(1, 'monthOffset') })), rangeColors: ['#'], color: '#', months: 2, weekStartsOn: 1, weekdayDisplayFormat: "eeeeee", monthDisplayFormat: "LLLL yyyy", showDateDisplay: false, preventSnapRefocus: true, fixedHeight: true, showMonthAndYearPickers: false, className: styles.container, locale: dateLocaleModule, classNames: {
                            ...styles,
                            // Exclude module `default` export from `styles` import
                            default: undefined,
                            monthsVertical: cn(styles.monthsVertical, {
                                [styles.withStaticRanges]: Boolean(createdStaticRanges?.length),
                            }),
                        } }), createdStaticRanges && (_jsx(DateRangePickerRanges, { startDate: dates.startDate, endDate: dates.endDate, staticRanges: createdStaticRanges, handleOnSelectRange: handleOnSelectRange })), _jsx(DialogBody, { children: _jsxs(DialogFooter, { direction: "row", children: [_jsx(Button, { onClick: onClose, variant: "tertiary", type: "button", children: cancelButtonLabel || i18n.t('cancel') }), _jsx(Button, { onClick: handleConfirmDate, type: "button", children: confirmButtonLabel || i18n.t('confirm') })] }) })] })] }));
};
//# sourceMappingURL=DateRangePicker.js.map